import {
	requestApiDelete,
	requestApiGet,
	requestApiGetFile,
	requestApiPatch,
	requestApiPost,
} from "../api/BacklogApiEndpoint";
import type { BacklogConfig } from "../contracts/BacklogConfig";
import type { BacklogApiContext } from "../contracts/api/BacklogApiContext";
import type { BacklogResponse } from "../contracts/api/BacklogResponse";
import type { BacklogResponseFile } from "../contracts/api/BacklogResponseFile";
import type { Category } from "../entities/Category";
import type { IssueTypeDetailed } from "../entities/IssueTypeDetailed";
import type { Priority } from "../entities/Priority";
import type { Project } from "../entities/Project";
import type { ProjectActivity } from "../entities/ProjectActivity";
import type { ProjectDiskUsage } from "../entities/ProjectDiskUsage";
import type { ProjectStatus } from "../entities/ProjectStatus";
import type { Resolution } from "../entities/Resolution";
import type { SharedFile } from "../entities/SharedFile";
import type { User } from "../entities/User";
import type { Version } from "../entities/Version";
import { BacklogError } from "../errors/BacklogError";
import type { EmptyObject } from "../types/EmptyObject";
import { assert } from "../utils/BacklogUtil";
import type { AddCategoryParams } from "./params/AddCategoryParams";
import type { AddIssueTypeParams } from "./params/AddIssueTypeParams";
import type { AddProjectAdministratorParams } from "./params/AddProjectAdministratorParams";
import type { AddProjectParams } from "./params/AddProjectParams";
import type { AddProjectUserParams } from "./params/AddProjectUserParams";
import type { AddStatusParams } from "./params/AddStatusParams";
import type { AddVersionMilestoneParams } from "./params/AddVersionMilestoneParams";
import type { DeleteCategoryParams } from "./params/DeleteCategoryParams";
import type { DeleteIssueTypeParams } from "./params/DeleteIssueTypeParams";
import type { DeleteProjectAdministratorParams } from "./params/DeleteProjectAdministratorParams";
import type { DeleteProjectParams } from "./params/DeleteProjectParams";
import type { DeleteProjectUserParams } from "./params/DeleteProjectUserParams";
import type { DeleteStatusParams } from "./params/DeleteStatusParams";
import type { DeleteVersionParams } from "./params/DeleteVersionParams";
import type { GetCategoryListParams } from "./params/GetCategoryListParams";
import type { GetFileParams } from "./params/GetFileParams";
import type { GetIssueTypeListParams } from "./params/GetIssueTypeListParams";
import type { GetListOfProjectAdministratorsParams } from "./params/GetListOfProjectAdministratorsParams";
import type { GetListOfSharedFilesParams } from "./params/GetListOfSharedFilesParams";
import type { GetProjectDiskUsageParams } from "./params/GetProjectDiskUsageParams";
import type { GetProjectIconParams } from "./params/GetProjectIconParams";
import type { GetProjectListParams } from "./params/GetProjectListParams";
import type { GetProjectParams } from "./params/GetProjectParams";
import type { GetProjectRecentUpdatesParams } from "./params/GetProjectRecentUpdatesParams";
import type { GetProjectUserListParams } from "./params/GetProjectUserListParams";
import type { GetStatusListOfProjectParams } from "./params/GetStatusListOfProjectParams";
import type { GetVersionMilestoneListParams } from "./params/GetVersionMilestoneListParams";
import type { UpdateCategoryParams } from "./params/UpdateCategoryParams";
import type { UpdateIssueTypeParams } from "./params/UpdateIssueTypeParams";
import type { UpdateOrderOfStatusParams } from "./params/UpdateOrderOfStatusParams";
import type { UpdateProjectParams } from "./params/UpdateProjectParams";
import type { UpdateStatusParams } from "./params/UpdateStatusParams";
import type { UpdateVersionMilestoneParams } from "./params/UpdateVersionMilestoneParams";

/**
 * Get Status List 状態一覧の取得
 *
 * Returns list of statuses.
 *
 * @deprecated This API has been deprecated and is no longer recommended for use. Please replace it with Get Status List of Project.
 */
export function getStatusList(
	config: BacklogConfig,
	params: EmptyObject,
	context?: BacklogApiContext,
): Promise<BacklogResponse<EmptyObject>> {
	throw new BacklogError("not supported.");
}

/**
 * Get Status List of Project プロジェクトの状態一覧の取得
 *
 * Returns list of status in the project.
 */
export function getStatusListOfProject(
	config: BacklogConfig,
	params: GetStatusListOfProjectParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<ProjectStatus[]>> {
	assert(params);
	return requestApiGet({
		config,
		path: "projects/:projectIdOrKey/statuses",
		params,
		context,
	});
}

/**
 * Get Priority List 優先度一覧の取得
 *
 * Returns list of priorities.
 */
export function getPriorityList(
	config: BacklogConfig,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Priority[]>> {
	return requestApiGet({
		config,
		path: "priorities",
		params: undefined,
		context,
	});
}

/**
 * Get Resolution List 完了理由一覧の取得
 *
 * Returns list of resolutions.
 */
export function getResolutionList(
	config: BacklogConfig,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Resolution[]>> {
	return requestApiGet({
		config,
		path: "resolutions",
		params: undefined,
		context,
	});
}

/**
 * Get Project List プロジェクト一覧の取得
 *
 * Returns list of projects.
 */
export function getProjectList(
	config: BacklogConfig,
	params?: GetProjectListParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Project[]>> {
	return requestApiGet({
		config,
		path: "projects",
		params,
		context,
	});
}

/**
 * Add Project プロジェクトの追加
 *
 * Adds new project.
 */
export function addProject(
	config: BacklogConfig,
	params: AddProjectParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Project>> {
	assert(params);
	return requestApiPost({
		config,
		path: "projects",
		params,
		context,
	});
}

/**
 * Get Project プロジェクト情報の取得
 *
 * Returns information about project.
 */
export function getProject(
	config: BacklogConfig,
	params: GetProjectParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Project>> {
	assert(params);
	return requestApiGet({
		config,
		path: "projects/:projectIdOrKey",
		params,
		context,
	});
}

/**
 * Update Project プロジェクト情報の更新
 *
 * Updates information about project.
 */
export function updateProject(
	config: BacklogConfig,
	params: UpdateProjectParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Project>> {
	assert(params);
	return requestApiPatch({
		config,
		path: "projects/:projectIdOrKey",
		params,
		context,
	});
}

/**
 * Delete Project プロジェクトの削除
 *
 * Deletes project.
 */
export function deleteProject(
	config: BacklogConfig,
	params: DeleteProjectParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Project>> {
	assert(params);
	return requestApiDelete({
		config,
		path: "projects/:projectIdOrKey",
		params,
		context,
	});
}

/**
 * Get Project Icon プロジェクトアイコンの取得
 *
 * Downloads project icon.
 */
export function getProjectIcon(
	config: BacklogConfig,
	params: GetProjectIconParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<BacklogResponseFile>> {
	assert(params);
	return requestApiGetFile({
		config,
		path: "projects/:projectIdOrKey/image",
		params,
		context,
	});
}

/**
 * Get Project Recent Updates プロジェクトの最近の活動の取得
 *
 * Returns recent update in the project.
 */
export function getProjectRecentUpdates(
	config: BacklogConfig,
	params: GetProjectRecentUpdatesParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<ProjectActivity[]>> {
	assert(params);
	return requestApiGet({
		config,
		path: "projects/:projectIdOrKey/activities",
		params,
		context,
	});
}

/**
 * Add Project User プロジェクトユーザーの追加
 *
 * Adds user to list of project members.
 */
export function addProjectUser(
	config: BacklogConfig,
	params: AddProjectUserParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<User>> {
	assert(params);
	return requestApiPost({
		config,
		path: "projects/:projectIdOrKey/users",
		params,
		context,
	});
}

/**
 * Get Project User List プロジェクトユーザー一覧の取得
 *
 * Returns list of project members.
 */
export function getProjectUserList(
	config: BacklogConfig,
	params: GetProjectUserListParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<User[]>> {
	assert(params);
	return requestApiGet({
		config,
		path: "projects/:projectIdOrKey/users",
		params,
		context,
	});
}

/**
 * Delete Project User プロジェクトユーザーの削除
 *
 * Removes user from list project members.
 */
export function deleteProjectUser(
	config: BacklogConfig,
	params: DeleteProjectUserParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<User>> {
	assert(params);
	return requestApiDelete({
		config,
		path: "projects/:projectIdOrKey/users",
		params,
		context,
	});
}

/**
 * Add Project Administrator プロジェクト管理者の追加
 *
 * Adds “Project Administrator” role to user
 */
export function addProjectAdministrator(
	config: BacklogConfig,
	params: AddProjectAdministratorParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<User>> {
	assert(params);
	return requestApiPost({
		config,
		path: "projects/:projectIdOrKey/administrators",
		params,
		context,
	});
}

/**
 * Get List of Project Administrators プロジェクト管理者一覧の取得
 *
 * Returns list of users who has Project Administrator role
 */
export function getListOfProjectAdministrators(
	config: BacklogConfig,
	params: GetListOfProjectAdministratorsParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<User[]>> {
	assert(params);
	return requestApiGet({
		config,
		path: "projects/:projectIdOrKey/administrators",
		params,
		context,
	});
}

/**
 * Delete Project Administrator プロジェクト管理者の削除
 *
 * Removes Project Administrator role from user
 */
export function deleteProjectAdministrator(
	config: BacklogConfig,
	params: DeleteProjectAdministratorParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<User>> {
	assert(params);
	return requestApiDelete({
		config,
		path: "projects/:projectIdOrKey/administrators",
		params,
		context,
	});
}

/**
 * Add Status 状態の追加
 *
 * Adds new Status to the project. You can create up to 8 custom statuses within a Project aside from the 4 default.
 */
export function addStatus(
	config: BacklogConfig,
	params: AddStatusParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<ProjectStatus>> {
	assert(params);
	// TODO: unconfirmed
	return requestApiPost({
		config,
		path: "projects/:projectIdOrKey/statuses",
		params,
		context,
	});
}

/**
 * Update Status 状態情報の更新
 *
 * Updates information about Status.
 */
export function updateStatus(
	config: BacklogConfig,
	params: UpdateStatusParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<ProjectStatus>> {
	assert(params);
	// TODO: unconfirmed
	return requestApiPatch({
		config,
		path: "projects/:projectIdOrKey/statuses/:id",
		params,
		context,
	});
}

/**
 * Delete Status 状態の削除
 *
 * Deletes Status.
 */
export function deleteStatus(
	config: BacklogConfig,
	params: DeleteStatusParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<ProjectStatus>> {
	assert(params);
	// TODO: unconfirmed
	return requestApiDelete({
		config,
		path: "projects/:projectIdOrKey/statuses/:id",
		params,
		context,
	});
}

/**
 * Update Order of Status 状態の並び替え
 *
 * Updates order about Status.
 */
export function updateOrderOfStatus(
	config: BacklogConfig,
	params: UpdateOrderOfStatusParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<ProjectStatus[]>> {
	assert(params);
	// TODO: unconfirmed
	return requestApiPatch({
		config,
		path: "projects/:projectIdOrKey/statuses/updateDisplayOrder",
		params,
		context,
	});
}

/**
 * Get Issue Type List 種別一覧の取得
 *
 * Returns list of Issue Types in the project.
 */
export function getIssueTypeList(
	config: BacklogConfig,
	params: GetIssueTypeListParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<IssueTypeDetailed[]>> {
	assert(params);
	return requestApiGet({
		config,
		path: "projects/:projectIdOrKey/issueTypes",
		params,
		context,
	});
}

/**
 * Add Issue Type 種別の追加
 *
 * Adds new Issue Type to the project.
 */
export function addIssueType(
	config: BacklogConfig,
	params: AddIssueTypeParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<IssueTypeDetailed>> {
	assert(params);
	return requestApiPost({
		config,
		path: "projects/:projectIdOrKey/issueTypes",
		params,
		context,
	});
}

/**
 * Update Issue Type 種別情報の更新
 *
 * Updates information about Issue Type.
 */
export function updateIssueType(
	config: BacklogConfig,
	params: UpdateIssueTypeParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<IssueTypeDetailed>> {
	assert(params);
	return requestApiPatch({
		config,
		path: "projects/:projectIdOrKey/issueTypes/:id",
		params,
		context,
	});
}

/**
 * Update Issue Type 種別情報の更新
 *
 * Updates information about Issue Type.
 */
export function deleteIssueType(
	config: BacklogConfig,
	params: DeleteIssueTypeParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<IssueTypeDetailed>> {
	assert(params);
	return requestApiDelete({
		config,
		path: "projects/:projectIdOrKey/issueTypes/:id",
		params,
		context,
	});
}

/**
 * Get Category List カテゴリー一覧の取得
 *
 * Returns list of Categories in the project.
 */
export function getCategoryList(
	config: BacklogConfig,
	params: GetCategoryListParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Category[]>> {
	assert(params);
	return requestApiGet({
		config,
		path: "projects/:projectIdOrKey/categories",
		params,
		context,
	});
}

/**
 * Add Category カテゴリーの追加
 *
 * Adds new Category to the project.
 */
export function addCategory(
	config: BacklogConfig,
	params: AddCategoryParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Category>> {
	assert(params);
	return requestApiPost({
		config,
		path: "projects/:projectIdOrKey/categories",
		params,
		context,
	});
}

/**
 * Update Category カテゴリー情報の更新
 *
 * Updates information about Category.
 */
export function updateCategory(
	config: BacklogConfig,
	params: UpdateCategoryParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Category>> {
	assert(params);
	return requestApiPatch({
		config,
		path: "projects/:projectIdOrKey/categories/:id",
		params,
		context,
	});
}

/**
 * Delete Category カテゴリーの削除
 *
 * Deletes Category.
 */
export function deleteCategory(
	config: BacklogConfig,
	params: DeleteCategoryParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Category>> {
	assert(params);
	return requestApiDelete({
		config,
		path: "projects/:projectIdOrKey/categories/:id",
		params,
		context,
	});
}

/**
 * Get Version/Milestone List バージョン(マイルストーン)一覧の取得
 *
 * Returns list of Versions/Milestones in the project.
 */
export function getVersionMilestoneList(
	config: BacklogConfig,
	params: GetVersionMilestoneListParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Version[]>> {
	assert(params);
	return requestApiGet({
		config,
		path: "projects/:projectIdOrKey/versions",
		params,
		context,
	});
}

/**
 * Add Version/Milestone バージョン(マイルストーン)の追加
 *
 * Adds new Version/Milestone to the project.
 */
export function addVersionMilestone(
	config: BacklogConfig,
	params: AddVersionMilestoneParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Version>> {
	assert(params);
	return requestApiPost({
		config,
		path: "projects/:projectIdOrKey/versions",
		params,
		context,
	});
}

/**
 * Update Version/Milestone バージョン(マイルストーン)情報の更新
 *
 * Updates information about Version/Milestone.
 */
export function updateVersionMilestone(
	config: BacklogConfig,
	params: UpdateVersionMilestoneParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Version>> {
	assert(params);
	return requestApiPatch({
		config,
		path: "projects/:projectIdOrKey/versions/:id",
		params,
		context,
	});
}

/**
 * Delete Version バージョン(マイルストーン)の削除
 *
 * Deletes Version.
 */
export function deleteVersion(
	config: BacklogConfig,
	params: DeleteVersionParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Version>> {
	assert(params);
	return requestApiDelete({
		config,
		path: "projects/:projectIdOrKey/versions/:id",
		params,
		context,
	});
}

/**
 * Get Custom Field List カスタム属性一覧の取得
 *
 * Returns list of Custom Fields in the project.
 *
 */
export function getCustomFieldList(
	config: BacklogConfig,
	params: EmptyObject,
	context?: BacklogApiContext,
): Promise<BacklogResponse<EmptyObject>> {
	throw new BacklogError("Method not implemented.");
}

/**
 * Add Custom Field カスタム属性の追加
 *
 * Adds new Custom Field to the project.
 */
export function addCustomField(
	config: BacklogConfig,
	params: EmptyObject,
	context?: BacklogApiContext,
): Promise<BacklogResponse<EmptyObject>> {
	throw new BacklogError("Method not implemented.");
}

/**
 * Update Custom Field カスタム属性の更新
 *
 * Updates Custom Field.
 */
export function updateCustomField(
	config: BacklogConfig,
	params: EmptyObject,
	context?: BacklogApiContext,
): Promise<BacklogResponse<EmptyObject>> {
	throw new BacklogError("Method not implemented.");
}

/**
 * Delete Custom Field カスタム属性の削除
 *
 * Deletes Custom Field.
 */
export function deleteCustomField(
	config: BacklogConfig,
	params: EmptyObject,
	context?: BacklogApiContext,
): Promise<BacklogResponse<EmptyObject>> {
	throw new BacklogError("Method not implemented.");
}

/**
 * Add List Item for List Type Custom Field 選択リストカスタム属性のリスト項目の追加
 *
 * Adds new list item for list type custom field.
 * Only administrator can call this API if the option “Add items in adding or editing issues” is disabled in settings.
 * Calling API fails if specified custom field’s type is not a list.
 */
export function addListItemForListTypeCustomField(
	config: BacklogConfig,
	params: EmptyObject,
	context?: BacklogApiContext,
): Promise<BacklogResponse<EmptyObject>> {
	throw new BacklogError("Method not implemented.");
}

/**
 * Update List Item for List Type Custom Field 選択リストカスタム属性のリスト項目の更新
 *
 * Updates list item for list type custom field. Calling API fails if specified custom field’s type is not a list.
 */
export function updateListItemForListTypeCustomField(
	config: BacklogConfig,
	params: EmptyObject,
	context?: BacklogApiContext,
): Promise<BacklogResponse<EmptyObject>> {
	throw new BacklogError("Method not implemented.");
}

/**
 * Delete List Item for List Type Custom Field 選択リストカスタム属性のリスト項目の削除
 *
 * Deletes list item for list type custom field. Calling API fails if specified custom field’s type is not a list.
 */
export function deleteListItemForListTypeCustomField(
	config: BacklogConfig,
	params: EmptyObject,
	context?: BacklogApiContext,
): Promise<BacklogResponse<EmptyObject>> {
	throw new BacklogError("Method not implemented.");
}

/**
 * Get List of Shared Files 共有ファイル一覧の取得
 *
 * Gets list of Shared Files.
 */
export function getListOfSharedFiles(
	config: BacklogConfig,
	params: GetListOfSharedFilesParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<SharedFile[]>> {
	assert(params);
	return requestApiGet({
		config,
		path: "projects/:projectIdOrKey/files/metadata/:path",
		params,
		context,
	});
}

/**
 * Get File 共有ファイルのダウンロード
 *
 * Downloads the file.
 */
export function getFile(
	config: BacklogConfig,
	params: GetFileParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<BacklogResponseFile>> {
	assert(params);
	return requestApiGetFile({
		config,
		path: "projects/:projectIdOrKey/files/:sharedFileId",
		params,
		context,
	});
}

/**
 * Get Project Disk Usage プロジェクトの容量使用状況の取得
 *
 * Returns information about project disk usage.
 */
export function getProjectDiskUsage(
	config: BacklogConfig,
	params: GetProjectDiskUsageParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<ProjectDiskUsage>> {
	assert(params);
	return requestApiGet({
		config,
		path: "projects/:projectIdOrKey/diskUsage",
		params,
		context,
	});
}
